import { useCallback, useEffect } from 'react'

import { Statement } from '@circlefin/components'
import { Base } from '@modals/layout'
import { PropsErrorBoundary } from '@shared/components/errors'
import { useSwitchEntityMutation } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { EntitySwitcherError } from './EntitySwitcher.Error'

export interface EntitySwitcherProps {
  /**
   * Requested Entity.
   */
  requestedEntityId?: string
  /**
   * Route to navigate to after switching entity.
   */
  route?: string
}

export const EntitySwitcher: React.FC<EntitySwitcherProps> = ({
  requestedEntityId,
  route,
}) => {
  const { t } = useTranslation('manageAccounts')

  const [switchEntity, { error }] = useSwitchEntityMutation({
    // Errors are handled via the `error` state returned by this hook.
    onError: () => {},
  })

  const switchEntityAndRedirect = useCallback(
    async (requestedEntityId: string, route: string) => {
      try {
        const result = await switchEntity({
          variables: {
            entityId: requestedEntityId,
          },
        })

        if (result.errors || result.data == null) {
          return
        }

        window.location.replace(route)
      } catch {
        // Errors are handled via the `error` state returned by the useSwitchEntityMutation hook.
      }
    },
    [switchEntity],
  )

  const handleRetry = useCallback(
    (requestedEntityId: string, route: string) => () => {
      void switchEntityAndRedirect(requestedEntityId, route)
    },
    [switchEntityAndRedirect],
  )

  useEffect(() => {
    // trigger switch entity if requested entity id and route are provided when modal is opened
    if (requestedEntityId && route) {
      void switchEntityAndRedirect(requestedEntityId, route)
    }
  }, [requestedEntityId, route, switchEntityAndRedirect])

  return (
    <Base variant="center">
      <PropsErrorBoundary<EntitySwitcherProps>
        props={{ requestedEntityId, route }}
      >
        {({ requestedEntityId, route }) => (
          <EntitySwitcherError
            error={error}
            handleRetry={handleRetry(requestedEntityId, route)}
          >
            <Statement
              iconName="CircleSpinnerSolid"
              padded={false}
              title={t`switchEntity.pending`}
              variant="component"
              loop
            />
          </EntitySwitcherError>
        )}
      </PropsErrorBoundary>
    </Base>
  )
}
